import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Clock, User } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { ClosingCTA } from "@/components/common/ClosingCTA";
import { BLOG, IMAGES } from "@/constants";
import { Seo } from "@/components/common/Seo";
import { cn } from "@/lib/utils";
import { blogService, categoryService } from "@/services/blogService"; // apna path daal lena
import SEO from "@/components/SEO";

const LIMIT = 9;

// 1 ... 4 5 6 ... 10 type page list
function getPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push("...");
    out.push(p);
  });
  return out;
}

function CardSkeleton() {
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden animate-pulse">
      <div className="h-52 sm:h-56 lg:h-60 bg-slate-200" />
      <div className="p-6 space-y-3">
        <div className="h-4 bg-slate-200 rounded w-3/4" />
        <div className="h-3 bg-slate-200 rounded" />
        <div className="h-3 bg-slate-200 rounded w-5/6" />
        <div className="h-3 bg-slate-200 rounded w-1/2 mt-6" />
      </div>
    </div>
  );
}

export default function BlogPage() {
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("all"); // category slug ya "all"
  const [page, setPage] = useState(1);
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const gridRef = useRef(null);

  // categories (ek baar)
  useEffect(() => {
    categoryService
      .getAll()
      .then((res) => setCategories(res.data || []))
      .catch(() => setCategories([]));
  }, []);

  // blog cards (category ya page badalne par)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    const params = { page, limit: LIMIT };
    if (active !== "all") params.category = active;

    blogService
      .getCards(params)
      .then((res) => {
        if (cancelled) return;
        setPosts(res.data?.blogs || []);
        setPagination(res.data?.pagination || { page: 1, pages: 1, total: 0 });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || "Blogs load nahi ho paaye. Dobara try karo.");
        setPosts([]);
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [active, page]);

  const changeCategory = (slug) => {
    setActive(slug);
    setPage(1);
  };

  const goToPage = (p) => {
    if (p < 1 || p > pagination.pages || p === page) return;
    setPage(p);
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const tags = [{ name: "All", slug: "all" }, ...categories];

  return (
    <>
    <SEO page="blog" />
    <div data-testid="blog-page">
      <Seo title="Blog & Insights" path="/blog" description="Career readiness, employer expectations, exam preparation, and stories from the CET community." />
      <PageHero title={BLOG.hero.heading} subtitle={BLOG.hero.body} />

      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter tags */}
          <div className="flex flex-wrap gap-2.5">
            {tags.map((t) => (
              <button
                key={t.slug}
                onClick={() => changeCategory(t.slug)}
                data-testid={`blog-filter-${t.slug}`}
                className={cn(
                  "rounded-full px-4 py-1.5 text-sm font-medium border transition-colors",
                  active === t.slug
                    ? "bg-[#F5A623] border-[#F5A623] text-slate-950"
                    : "bg-white border-slate-300 text-slate-600 hover:border-[#F5A623]"
                )}
              >
                {t.name}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div ref={gridRef} className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 scroll-mt-24">
            {loading &&
              Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}

            {!loading && error && (
              <p className="col-span-full text-center text-slate-500 py-12">{error}</p>
            )}

            {!loading && !error && posts.length === 0 && (
              <p className="col-span-full text-center text-slate-500 py-12">
                Is category me abhi koi blog nahi hai.
              </p>
            )}

            {!loading &&
              posts.map((p, i) => (
                <Link
                  to={`/blog/${p.slug}`}
                  key={p._id}
                  className="group flex flex-col rounded-xl border border-slate-200 bg-white overflow-hidden hover:border-[#F5A623] hover:shadow-md transition-colors"
                  data-testid={`blog-post-${i}`}
                >
             <div className="h-52 sm:h-56 lg:h-60 relative overflow-hidden bg-slate-100">
                    <img
                      src={p.image || IMAGES.laptopStudy}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
                    <span className="absolute bottom-3 left-4 text-[11px] font-bold uppercase tracking-widest text-[#F5A623]">
                      {p.category?.name}
                    </span>
                  </div>

                  <div className="flex flex-col flex-1 p-6">
                    <h3 className="font-heading font-bold text-slate-900 leading-snug group-hover:text-amber-700 transition-colors line-clamp-2">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1 line-clamp-3">
                      {p.shortDescription}
                    </p>

                    <div className="mt-4 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-3 text-slate-500 min-w-0">
                        <span className="inline-flex items-center gap-1 min-w-0">
                          <User className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{p.authorName}</span>
                        </span>
                        <span className="inline-flex items-center gap-1 shrink-0">
                          <Clock className="h-3.5 w-3.5" />
                          {p.readTime} min read
                        </span>
                      </div>
                      <span className="inline-flex items-center gap-1 font-semibold text-amber-600 shrink-0 ml-3">
                        Read <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
          </div>

          {/* Pagination */}
          {!loading && pagination.pages > 1 && (
            <nav className="mt-12 flex items-center justify-center gap-1.5" aria-label="Pagination" data-testid="blog-pagination">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page === 1}
                className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-slate-300 text-slate-600 hover:border-[#F5A623] disabled:opacity-40 disabled:hover:border-slate-300"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              {getPageNumbers(page, pagination.pages).map((n, i) =>
                n === "..." ? (
                  <span key={`dots-${i}`} className="px-1 text-slate-400">…</span>
                ) : (
                  <button
                    key={n}
                    onClick={() => goToPage(n)}
                    className={cn(
                      "h-9 min-w-9 px-3 rounded-lg border text-sm font-medium transition-colors",
                      n === page
                        ? "bg-[#F5A623] border-[#F5A623] text-slate-950"
                        : "border-slate-300 text-slate-600 hover:border-[#F5A623]"
                    )}
                    aria-current={n === page ? "page" : undefined}
                  >
                    {n}
                  </button>
                )
              )}

              <button
                onClick={() => goToPage(page + 1)}
                disabled={page === pagination.pages}
                className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-slate-300 text-slate-600 hover:border-[#F5A623] disabled:opacity-40 disabled:hover:border-slate-300"
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </nav>
          )}
        </div>
      </section>

      <ClosingCTA />
    </div>
    </>
  );
}